'use client';

import React, { createContext, useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import type { MomentCollection } from '@/lib/services/collection-service';

export type CollectionsContextType = {
  collections: MomentCollection[];
  sharedCollections: MomentCollection[];
  isLoading: boolean;
  error: string | null;
  activeDomainId: string | null;
  selectedCollectionId: string | null;
  setSelectedCollectionId: (id: string | null) => void;
  refetchCollections: () => Promise<void>;
  createCollection: (name: string, options?: any) => Promise<MomentCollection | null>;
  deleteCollection: (collectionId: string) => Promise<boolean>;
};

const CollectionsContext = createContext<CollectionsContextType | undefined>(undefined);

interface CollectionsProviderProps {
  children: React.ReactNode;
  domainId: string;
}

export const CollectionsProvider: React.FC<CollectionsProviderProps> = ({ children, domainId }) => {
  const pathname = usePathname();
  const [collections, setCollections] = useState<MomentCollection[]>([]);
  const [sharedCollections, setSharedCollections] = useState<MomentCollection[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [activeDomainId, setActiveDomainId] = useState<string | null>(domainId);

  const fetchCollections = useCallback(async () => {
    if (!domainId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const [ownedResponse, sharedResponse] = await Promise.all([
        fetch(`/api/collections?domainId=${domainId}`),
        fetch(`/api/collections/shared?domainId=${domainId}`)
      ]);

      if (!ownedResponse.ok || !sharedResponse.ok) {
        throw new Error('Failed to fetch collections');
      }

      const ownedData = await ownedResponse.json();
      const sharedData = await sharedResponse.json();

      setCollections(ownedData.collections || []);
      setSharedCollections(sharedData.collections?.map((c: any) => c.collection) || []);
    } catch (err: any) {
      console.error('[Collections] Error fetching collections:', err);
      setError(err.message || 'Failed to load collections');
      setCollections([]);
      setSharedCollections([]);
    } finally {
      setIsLoading(false);
    }
  }, [domainId]);

  useEffect(() => {
    if (domainId !== activeDomainId) {
      console.log('[Collections] Domain changed from', activeDomainId, 'to', domainId);
      setActiveDomainId(domainId);
      setSelectedCollectionId(null);
      fetchCollections();
    }
  }, [domainId, activeDomainId, fetchCollections]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const refetchCollections = useCallback(async () => {
    await fetchCollections();
  }, [fetchCollections]);

  const createCollection = useCallback(async (
    name: string,
    options?: {
      description?: string;
      color?: string;
      icon?: string;
    }
  ): Promise<MomentCollection | null> => {
    if (!domainId) return null;

    try {
      const response = await fetch('/api/collections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domainId,
          name,
          ...options,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create collection');
      }

      const data = await response.json();
      const newCollection = data.collection;

      setCollections(prev => [...prev, newCollection]);

      return newCollection;
    } catch (err: any) {
      console.error('[Collections] Error creating collection:', err);
      setError(err.message || 'Failed to create collection');
      return null;
    }
  }, [domainId]);

  const deleteCollection = useCallback(async (collectionId: string): Promise<boolean> => {
    try {
      const response = await fetch(`/api/collections/${collectionId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete collection');
      }

      setCollections(prev => prev.filter(c => c.id !== collectionId));

      if (selectedCollectionId === collectionId) {
        setSelectedCollectionId(null);
      }

      return true;
    } catch (err: any) {
      console.error('[Collections] Error deleting collection:', err);
      setError(err.message || 'Failed to delete collection');
      return false;
    }
  }, [selectedCollectionId]);

  const contextValue = useMemo(() => ({
    collections,
    sharedCollections,
    isLoading,
    error,
    activeDomainId,
    selectedCollectionId,
    setSelectedCollectionId,
    refetchCollections,
    createCollection,
    deleteCollection,
  }), [
    collections,
    sharedCollections,
    isLoading,
    error,
    activeDomainId,
    selectedCollectionId,
    refetchCollections,
    createCollection,
    deleteCollection,
  ]);

  return (
    <CollectionsContext.Provider value={contextValue}>
      {children}
    </CollectionsContext.Provider>
  );
};

export const useCollections = () => {
  const context = useContext(CollectionsContext);

  if (context === undefined) {
    throw new Error('useCollections must be used within a CollectionsProvider');
  }

  return context;
};

export default CollectionsContext;
